/** Derived on the server from subscriptions/orders; the list may grow (see WebPaymentsRepository). */
export type WebUserSubscriptionStatus = 'never' | 'active' | 'unsubscribed' | 'purchased';

/** Where a login has actually been used; mirrors WebUserSurface in the backend entity. */
export type WebUserSurface = 'funnel' | 'ios' | 'android' | 'editor';

/** Per-surface counters kept in web_user.meta. */
export interface WebUserSurfaceUsage {
    firstSeenAt: string;
    lastSeenAt: string;
    count: number;
}

/** Coarse bucket derived from the surfaces on the server. */
export type WebUserPresence = 'only_funnel' | 'funnel_and_app' | 'only_app' | 'unknown';

/** Display order of the surface badges: the funnel comes first, then the apps. */
export const WEB_USER_SURFACES: WebUserSurface[] = ['funnel', 'ios', 'android', 'editor'];

/** 'editor' is shown as 'web' — for the reader it is simply the browser, not a fourth platform. */
export const WEB_USER_SURFACE_LABELS: {[surface: string]: string} = {
  funnel: 'w2w',
  ios: 'ios',
  android: 'android',
  editor: 'web',
};

export const WEB_USER_PRESENCE_LABELS: {[presence: string]: string} = {
  only_funnel: 'w2w only',
  funnel_and_app: 'w2w + app',
  only_app: 'app only',
  unknown: 'unknown',
};

/** Order of the presence filter toggles, matching the funnel-to-app progression. */
export const WEB_USER_PRESENCES: WebUserPresence[] = ['only_funnel', 'funnel_and_app', 'only_app', 'unknown'];

/** One row of the users section, as returned by the GET_WEB_USERS websocket action. */
export interface WebUserRow {
    id: string;
    email: string | null;
    createdAt: string;
    hasPurchase: boolean;
    /** Last change of the payment status; null for users who never paid. */
    statusChangedAt: string | null;
    subscriptionStatus: WebUserSubscriptionStatus;
    /** Best-known language (browser > system > app); the server defaults it to 'en'. */
    language: string;
    source: string;
    /** Keyed by surface; null for users who logged in before surface tracking existed. */
    surfaces: {[surface: string]: WebUserSurfaceUsage} | null;
    presence: WebUserPresence;
}

/** Keyset cursor — the last row of the page already loaded. */
export interface WebUsersCursor {
    createdAt: string;
    id: string;
}

/**
 * Both ranges are inclusive at the start and exclusive at the end, and are applied on the server:
 * `created` filters web_user.createdAt, `statusChanged` the latest payment status change.
 */
export interface WebUsersFilter {
    createdFrom: string | null;
    createdTo: string | null;
    statusChangedFrom: string | null;
    statusChangedTo: string | null;
    /** Presence buckets to keep; null (or empty) means every bucket. Applied on the server. */
    presence: WebUserPresence[] | null;
}

export const emptyWebUsersFilter = (): WebUsersFilter => ({
  createdFrom: null,
  createdTo: null,
  statusChangedFrom: null,
  statusChangedTo: null,
  presence: null,
});

/** Surfaces of a row in display order, skipping the ones it was never seen on. */
export const rowSurfaces = (user: WebUserRow): WebUserSurface[] =>
  WEB_USER_SURFACES.filter((surface) => !!user.surfaces?.[surface]);
